import { SearchIcon } from '@twistopayments/design-system/icons/inline'

import type { CategoryAPI, SectionsEshopsComponent } from '@twisto/api-cms'
import { useAppConfig } from '@twisto/base'

import { AccessibleDiv } from 'homepage/components/atoms/accessible-div'
import { Hidden } from 'homepage/components/atoms/hidden'
import { useAdditionalData } from 'homepage/libs/context'

import { CategoriesDropdown } from './categories-dropdown'
import {
  getEshopsNavigation,
  getSelectionKey,
} from './eshops-navigation.helpers'
import * as styles from './eshops-navigation.styles'
import type { EshopSelection } from './types'

type Props = {
  categories: CategoryAPI[]
  selection: EshopSelection
  navigation?: SectionsEshopsComponent['navigation']
  hasSearch?: boolean
  onSelect: (selection: EshopSelection) => void
  handleSearch?: (searchTerm: string) => void
}

const EshopsNavigation = ({
  selection,
  navigation,
  categories,
  hasSearch,
  onSelect,
  handleSearch,
}: Props) => {
  const { staticTexts } = useAdditionalData()
  const { allEshops, moreCategories, search, payInThree } = staticTexts || {}
  const { region } = useAppConfig()

  const { items, remainingCategories } = getEshopsNavigation(
    categories,
    region,
    navigation
  )
  const selectedKey = getSelectionKey(selection)

  return (
    <nav css={styles.navigation}>
      <div css={styles.navContainer}>
        <ul css={[styles.navList, styles.horizontalList]}>
          {items.map((item) => (
            <AccessibleDiv
              key={getSelectionKey(item)}
              as="li"
              css={[
                styles.listItem,
                selectedKey === getSelectionKey(item) && styles.activeItem,
              ]}
              role="button"
              onActivate={() => onSelect(item)}
            >
              {item.type === 'category'
                ? item.category.name
                : item.type === 'all'
                  ? allEshops
                  : payInThree}
            </AccessibleDiv>
          ))}

          <Hidden breakpoint="smUp">
            {remainingCategories.map((category) => (
              <AccessibleDiv
                key={category.id}
                as="li"
                css={[
                  styles.listItem,
                  selection.type === 'category' &&
                    selection.category.id === category.id &&
                    styles.activeItem,
                ]}
                role="button"
                onActivate={() => onSelect({ type: 'category', category })}
              >
                {category.name}
              </AccessibleDiv>
            ))}
          </Hidden>

          {!!remainingCategories.length && (
            <Hidden breakpoint="smDown">
              <CategoriesDropdown
                categories={remainingCategories}
                changeCategory={(category) =>
                  onSelect({ type: 'category', category })
                }
                currentCategory={
                  selection.type === 'category' ? selection.category : null
                }
                label={moreCategories}
              />
            </Hidden>
          )}
        </ul>

        {hasSearch && handleSearch && (
          <div css={styles.searchWrap}>
            <SearchIcon size="20" />
            <input
              css={styles.searchBar}
              placeholder={search}
              type="text"
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        )}
      </div>
    </nav>
  )
}

export default EshopsNavigation
